'use client';

import { useMemo } from 'react';

import { KeycloakService } from '@m/keycloak-service/KeycloakService';
import { useCustomerStore } from '@m/utils/zustand/customer-store';

import type { EvaluationContext } from '@m/services/experimentation/EvaluationContext';

/**
 * Builds an EvaluationContext with Keycloak email and the active tenant id
 * (`customIds.tenantID`), then merges any caller overrides on top.
 *
 * Does not set `userId` — Keycloak subjects are strings, while the API expects a
 * numeric Metropolis user id.
 */
export function useEvaluationContext(overrides?: EvaluationContext): EvaluationContext {
  const tenantId = useCustomerStore.use.customerId();

  return useMemo(() => {
    const { email } = KeycloakService.getUserObject();
    const keycloakEmail = typeof email === 'string' ? email : undefined;

    const customIds: Record<string, string | number | boolean> = {
      ...(tenantId ? { tenantID: tenantId } : {}),
      ...overrides?.customIds,
    };

    return {
      ...overrides,
      email: overrides?.email ?? keycloakEmail,
      ...(Object.keys(customIds).length > 0 ? { customIds } : {}),
    };
  }, [overrides, tenantId]);
}
